import {Component, DestroyRef, OnInit, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {forkJoin, of} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';

import {AuthService} from '../service/auth.service';
import {DashboardStatisticsService} from '../dashboard-analytics/services/dashboard-statistics.service';
import {DashboardOverviewStats} from '../dashboard-analytics/models/dashboard-statistics.model';
import {DashboardPeriodService, PeriodMode} from './services/dashboard-period.service';
import {MatchTuneDialogComponent} from './match-tune/match-tune-dialog/match-tune-dialog.component';
import {fadeInAnimation} from '../animations/fadeInAnimation';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  animations: [fadeInAnimation],
  host: {'[@fadeInAnimation]': ''}
})
export class DashboardComponent implements OnInit {

  private readonly authService = inject(AuthService);
  private readonly statsService = inject(DashboardStatisticsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  readonly periodService = inject(DashboardPeriodService);

  // KPI overview state — current + previous (previous is null in CUSTOM mode)
  readonly currentOverview = signal<DashboardOverviewStats | null>(null);
  readonly previousOverview = signal<DashboardOverviewStats | null>(null);
  readonly isOverviewLoading = signal<boolean>(false);

  // Convenience role flags
  readonly hasAdminAccess = computed(() =>
    this.authService.isAdmin() || this.authService.isSuperAdmin()
  );

  // Custom range form-bound dates (YYYY-MM-DD)
  customStartStr = '';
  customEndStr = '';

  ngOnInit(): void {
    this.customStartStr = this.formatInputDate(this.periodService.customStart());
    this.customEndStr = this.formatInputDate(this.periodService.customEnd());

    if (this.hasAdminAccess()) {
      this.loadOverview();
    }
  }

  // ── Period control ──────────────────────────────────────────

  setPeriod(p: PeriodMode): void {
    if (p === 'CUSTOM') {
      // Switching to custom: keep existing custom range, just flip mode
      this.periodService.setMode('CUSTOM');
    } else {
      this.periodService.setMode(p);
    }
    if (this.hasAdminAccess()) {
      this.loadOverview();
    }
  }

  onCustomStartChange(value: string): void {
    if (!value) return;
    this.customStartStr = value;
    this.applyCustomRange();
  }

  onCustomEndChange(value: string): void {
    if (!value) return;
    this.customEndStr = value;
    this.applyCustomRange();
  }

  private applyCustomRange(): void {
    if (!this.customStartStr || !this.customEndStr) return;
    const start = new Date(this.customStartStr);
    const end = new Date(this.customEndStr);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return;
    if (start.getTime() > end.getTime()) return;
    this.periodService.setCustomRange(start, end);
    if (this.hasAdminAccess()) {
      this.loadOverview();
    }
  }

  // ── Data load ───────────────────────────────────────────────

  private loadOverview(): void {
    this.isOverviewLoading.set(true);
    const start = this.periodService.currentStart();
    const end = this.periodService.currentEnd();
    const prevStart = this.periodService.previousStart();
    const prevEnd = this.periodService.previousEnd();

    forkJoin({
      current: this.statsService.getOverview(start, end),
      previous: prevStart && prevEnd
        ? this.statsService.getOverview(prevStart, prevEnd)
        : of(null)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({current, previous}) => {
          this.currentOverview.set(current);
          this.previousOverview.set(previous);
          this.isOverviewLoading.set(false);
        },
        error: () => this.isOverviewLoading.set(false)
      });
  }

  // ── Match and Tune dialog ───────────────────────────────────

  openMatchTuneDialog(file: File): void {
    this.dialog.open(MatchTuneDialogComponent, {
      data: {initialFile: file},
      panelClass: 'match-tune-dialog-panel',
      maxWidth: '95vw',
      width: 'auto',
      autoFocus: false,
      restoreFocus: true
    });
  }

  // ── Helpers ─────────────────────────────────────────────────

  private formatInputDate(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  // ── Role exposure for template ──────────────────────────────

  isLoggedIn() { return this.authService.isLoggedIn(); }
  isAdmin() { return this.authService.isAdmin(); }
  isSuperAdmin() { return this.authService.isSuperAdmin(); }
  isModerator() { return this.authService.isModerator(); }
  isUser() { return this.authService.isUser(); }
}
